/*
 * Raqs — PhotoSwipe v5 for Dawn's product gallery
 *
 * Opens instantly on the image the browser has already downloaded, then lets
 * PhotoSwipe pull larger renditions from Dawn's existing srcset as the user
 * zooms. Nothing extra is fetched just to open the viewer.
 *
 * Dimensions come from data-pswp-width / data-pswp-height, published by
 * snippets/product-thumbnail.liquid from media.preview_image — the true source
 * size, not inferred from the DOM.
 *
 * Only elements carrying those attributes are picked up, so video and 3D media
 * keep Dawn's own handling.
 */

const paths = window.RAQS_PSWP;

if (!paths || !paths.core || !paths.lightbox) {
  console.warn('[raqs] window.RAQS_PSWP is missing — check sections/main-product.liquid');
} else {
  const { default: PhotoSwipeLightbox } = await import(paths.lightbox);

  const lightboxes = new WeakMap();

  /* Image media in DOM order. Dawn already omits variant-filtered images. */
  function mediaContainers(gallery) {
    return Array.from(gallery.querySelectorAll('.product-media-container[data-pswp-width]'));
  }

  function slidesFrom(gallery) {
    return mediaContainers(gallery)
      .map((container) => {
        const img = container.querySelector('.product__media img');
        if (!img) return null;

        // Whatever the browser actually picked for this viewport. Already in
        // cache, so it paints with no network round trip.
        const loaded = img.currentSrc || img.src;

        return {
          src: loaded,
          srcset: img.getAttribute('srcset') || undefined,
          msrc: loaded,
          width: Number(container.dataset.pswpWidth),
          height: Number(container.dataset.pswpHeight),
          alt: img.getAttribute('alt') || ''
        };
      })
      .filter(Boolean);
  }

  function lightboxFor(gallery) {
    if (lightboxes.has(gallery)) return lightboxes.get(gallery);

    const lightbox = new PhotoSwipeLightbox({
      pswpModule: () => import(paths.core),
      bgOpacity: 0.94,
      counter: true,
      zoom: true,
      wheelToZoom: true,
      returnFocus: false,
      arrowPrevSVG: '<svg class="pswp__icn" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M15 4 L7.5 12 L15 20" fill="none" stroke="#1a1a1a" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      arrowNextSVG: '<svg class="pswp__icn" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M9 4 L16.5 12 L9 20" fill="none" stroke="#1a1a1a" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      closeSVG: '<svg class="pswp__icn" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M5 5 L19 19 M19 5 L5 19" fill="none" stroke="#1a1a1a" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    });

    lightbox.init();

    let opener = null;

    lightbox.on('beforeOpen', () => {
      opener = document.activeElement?.closest('.product-media-container') || null;
    });

    lightbox.on('destroy', () => {
      if (!opener) return;
      opener.setAttribute('tabindex', '-1');
      opener.focus({ preventScroll: true });
    });

    lightboxes.set(gallery, lightbox);
    return lightbox;
  }

  document.addEventListener(
    'click',
    (event) => {
      const toggle = event.target.closest('.product__media-toggle');
      if (!toggle) return;

      const gallery = toggle.closest('media-gallery');
      const container = toggle.closest('.product-media-container');
      if (!gallery || !container || !container.dataset.pswpWidth) return;

      const slides = slidesFrom(gallery);
      if (!slides.length) return;

      event.preventDefault();
      event.stopPropagation();

      const index = Math.max(0, mediaContainers(gallery).indexOf(container));
      lightboxFor(gallery).loadAndOpen(index, slides);
    },
    true
  );
}